import { useState, useEffect } from "react";
import {
  ShoppingBagIcon,
  // ClockIcon,
  // CheckCircleIcon,
  HeartIcon,
  // MapPinIcon,
  CurrencyDollarIcon,
  TruckIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import { supabase, TABLES } from "../supabase";
import { Link } from "react-router-dom";
import { cn } from "../utils/cn";

interface RecipientStats {
  totalClaims: number;
  totalRequests: number;
  completedOrders: number;
  totalMealsReceived: number;
  moneySaved: number;
  favoriteCategories: string[];
}

export default function RecipientDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [claims, setClaims] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [stats, setStats] = useState<RecipientStats>({
    totalClaims: 0,
    totalRequests: 0,
    completedOrders: 0,
    totalMealsReceived: 0,
    moneySaved: 0,
    favoriteCategories: [],
  });
  const [loading, setLoading] = useState(true);
  const [_notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    if (user) {
      fetchRecipientData();
      fetchNotifications();

      // Set up real-time subscriptions for food claims
      const claimsSubscription = supabase
        .channel("food_claims_recipient")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: TABLES.FOOD_CLAIMS,
            filter: `claimant_id=eq.${user.id}`,
          },
          (payload) => {
            console.log("Food claim status changed:", payload);
            // Refresh data when claims change
            fetchRecipientData();
          }
        )
        .subscribe();

      // Set up real-time subscriptions for notifications
      const notificationsSubscription = supabase
        .channel("notifications_recipient")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: TABLES.NOTIFICATIONS,
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            fetchNotifications();
          }
        )
        .subscribe();

      return () => {
        claimsSubscription.unsubscribe();
        notificationsSubscription.unsubscribe();
      };
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data: notificationsData, error } = await supabase
        .from(TABLES.NOTIFICATIONS)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setNotifications(notificationsData || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };
  const fetchRecipientData = async () => {
    if (!user) return;

    try {
      // Fetch claims
      const { data: claimsData, error: claimsError } = await supabase
        .from(TABLES.FOOD_CLAIMS)
        .select("*")
        .eq("claimant_id", user.id);

      if (claimsError) throw claimsError;

      const processedClaims = (claimsData || []).map((data) => ({
        id: data.id,
        status: data.status || "",
        createdAt: data.created_at || null,
        pickupDateTime: data.pickup_date_time || null,
        contactMethod: data.contact_method || "",
        notes: data.notes || "",
        ...data,
      }));

      // Fetch requests
      const { data: requestsData, error: requestsError } = await supabase
        .from(TABLES.FOOD_REQUESTS)
        .select("*")
        .eq("requester_id", user.id);

      if (requestsError) throw requestsError;

      const processedRequests = (requestsData || []).map((data) => ({
        id: data.id,
        status: data.status || "",
        createdAt: data.created_at || null,
        quantity: data.quantity || 1,
        totalPrice: data.total_price || 0,
        paymentMethod: data.payment_method || "",
        ...data,
      }));

      // Fetch deliveries
      const { data: deliveriesData, error: deliveriesError } = await supabase
        .from(TABLES.DELIVERY_REQUESTS)
        .select("*")
        .eq("requester_id", user.id);

      if (deliveriesError) throw deliveriesError;

      const processedDeliveries = (deliveriesData || []).map((data) => ({
        id: data.id,
        status: data.status || "",
        createdAt: data.created_at || null,
        deliveryAddress: {
          address: data.delivery_address,
          city: data.city,
          district: data.district,
          postalCode: data.postal_code,
        },
        deliveryDateTime: data.delivery_date_time || null,
        deliveryFee: data.delivery_fee || 0,
        ...data,
      }));

      setClaims(processedClaims);
      setRequests(processedRequests);
      setDeliveries(processedDeliveries);

      // Calculate stats
      const totalClaims = processedClaims.length;
      const totalRequests = processedRequests.length;
      const completedOrders = [...processedClaims, ...processedRequests].filter(
        (item) => item.status === "completed"
      ).length;

      const totalMealsReceived =
        processedRequests.reduce((sum, req) => sum + (req.quantity || 1), 0) +
        processedClaims.length;
      const moneySaved = processedRequests.reduce(
        (sum, req) => sum + (req.totalPrice || 0),
        0
      );

      setStats({
        totalClaims,
        totalRequests,
        completedOrders,
        totalMealsReceived,
        moneySaved,
        favoriteCategories: [
          "Fruits & Vegetables",
          "Bakery Items",
          "Prepared Meals",
        ],
      });
    } catch (error) {
      console.error("Error fetching recipient data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (date: any) => {
    if (!date) return "N/A";
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  const tabs = [
    { id: "overview", name: "Overview", icon: HeartIcon },
    { id: "claims", name: "My Claims", icon: ShoppingBagIcon },
    { id: "requests", name: "My Requests", icon: CurrencyDollarIcon },
    { id: "deliveries", name: "Deliveries", icon: TruckIcon },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          <p className="text-xl font-bold text-slate-700">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-2xl shadow-2xl border-b border-white/30">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">
                Recipient Dashboard
              </h1>
              <p className="text-base text-slate-600 font-medium">
                Welcome back, {user?.user_metadata?.full_name || user?.email}
              </p>
            </div>
            <Link
              to="/find-food"
              className="flex items-center space-x-2 btn-primary"
            >
              <ShoppingBagIcon className="w-4 h-4" />
              <span>Find Food</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="card-dashboard">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <ShoppingBagIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">
                  Total Claims
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {stats.totalClaims}
                </p>
              </div>
            </div>
          </div>

          <div className="card-dashboard">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CurrencyDollarIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">
                  Total Requests
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {stats.totalRequests}
                </p>
              </div>
            </div>
          </div>

          <div className="card-dashboard">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <HeartIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">
                  Meals Received
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {stats.totalMealsReceived}
                </p>
              </div>
            </div>
          </div>

          <div className="card-dashboard">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <StarIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-gray-600">
                  Money Saved
                </p>
                <p className="text-xl font-bold text-gray-900">
                  LKR {stats.moneySaved.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex flex-wrap gap-2 p-1 bg-white/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/30">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center space-x-2 py-2 px-4 rounded-lg font-semibold text-sm",
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md"
                    : "text-slate-600"
                )}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* <div className="card">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Your Impact
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Food Waste Prevented:</span>
                    <span className="font-medium">
                      {(stats.totalMealsReceived * 0.3).toFixed(1)} kg
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CO₂ Saved:</span>
                    <span className="font-medium">
                      {(stats.totalMealsReceived * 0.5).toFixed(1)} kg
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Community Rank:</span>
                    <span className="font-medium">Active Member</span>
                  </div>
                </div>
              </div> */}

              <div className="card-dashboard">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Favorite Categories
                </h3>
                <div className="space-y-3">
                  {stats.favoriteCategories.map((category, index) => (
                    <div
                      key={category}
                      className="flex items-center justify-between"
                    >
                      <span className="text-gray-700">{category}</span>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon
                            key={i}
                            className={cn(
                              "w-4 h-4",
                              i < 5 - index
                                ? "text-yellow-400 fill-current"
                                : "text-gray-300"
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card-dashboard">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Recent Activity
              </h3>
              <div className="space-y-4">
                {[...claims, ...requests].slice(0, 5).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100">
                        {item.quantity ? (
                          <CurrencyDollarIcon className="w-5 h-5 text-primary-600" />
                        ) : (
                          <ShoppingBagIcon className="w-5 h-5 text-primary-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.quantity
                            ? `Requested ${item.quantity} servings`
                            : "Claimed free food"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(item.createdAt)}
                        </p>
                      </div>
                    </div>
                    <span
                      className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        getStatusColor(item.status)
                      )}
                    >
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "claims" && (
          <div className="card-dashboard">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Claim Details
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Pickup Date
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Contact Method
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Date Claimed
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {claims.map((claim) => (
                    <tr key={claim.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Food Claim
                        </div>
                        <div className="text-sm text-gray-500">
                          {claim.notes || "No notes"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(claim.pickupDateTime)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {claim.contactMethod}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            getStatusColor(claim.status)
                          )}
                        >
                          {claim.status === "confirmed"
                            ? "approved"
                            : claim.status === "cancelled"
                            ? "declined"
                            : claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(claim.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "requests" && (
          <div className="card-dashboard">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Request Details
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Total Price
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Payment Method
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Food Request
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(request.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {request.quantity} servings
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        LKR {request.totalPrice?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {request.paymentMethod}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            getStatusColor(request.status)
                          )}
                        >
                          {request.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "deliveries" && (
          <div className="card-dashboard">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Delivery Address
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Delivery Date
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Delivery Fee
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Requested Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deliveries.map((delivery) => (
                    <tr key={delivery.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {delivery.deliveryAddress?.address}
                        </div>
                        <div className="text-sm text-gray-500">
                          {delivery.deliveryAddress?.city},{" "}
                          {delivery.deliveryAddress?.district}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(delivery.deliveryDateTime)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        LKR {delivery.deliveryFee?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            getStatusColor(delivery.status)
                          )}
                        >
                          {delivery.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(delivery.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
